/**
 * Outil « Planche de Galton » (Boîte à outils, 1re → prépa).
 *
 * On règle n (rangées de clous) et p (chance d'aller à droite), on lâche 1 à
 * 1 000 billes : l'histogramme des casiers se remplit sur la loi B(n, p)
 * dessinée en pointillés. Le casier k = nombre de succès ; P(X = k), E = np
 * et σ s'affichent. Dès la Tle : bande de Bienaymé-Tchebychev |X − E| ≥ 2σ,
 * dont la probabilité reste sous 1/4. En 1re, n ≤ 4 (arbres de Bernoulli).
 * Calculs : `galton.ts` ; tirages : `ui/random.ts`.
 */

import { useMemo, useState } from 'react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { MathText, cn } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import {
  Note,
  ParamSlider,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  Stepper,
  ToggleChip,
} from '../../ui/controls';
import { formatNumber, numberAtoms } from '../../ui/format';
import { rational } from '../../ui/exact';
import { AXIS, FIGURE_BG_CLASS, INK } from '../../ui/svg-parts';
import {
  binomCoeff,
  binomialStats,
  chebyshev,
  dropBalls,
  emptyBoard,
  exactPmf,
  MAX_BALLS,
  sampleMean,
  type GaltonState,
} from './galton';

const W = 320;
const BAR = 'var(--color-primary-500)';
const LAW = 'var(--color-xp-500)';
const PATH = 'var(--color-accent-500)';
const OUT = 'var(--color-error-500)';
const BATCHES = [1, 10, 100, 1000] as const;

const count = (n: number) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
/** 1,5 × 10⁻²¹ en KaTeX */
const texSci = (v: number) => {
  const [m, e] = v.toExponential(1).split('e');
  return `${m!.replace('.', '{,}')} \\times 10^{${Number(e)}}`;
};
const tx = (s: string) => s.replace(/,/g, '{,}').replace(/−/g, '-');

export function GaltonBoardTool({
  onSend,
  sendCost,
  level,
  rng = Math.random,
}: ToolProps & { level?: string | null; rng?: () => number }) {
  const rank = schoolLevelRank(level);
  const premiere = rank === 6;
  const maxN = premiere ? 4 : 16;
  const [n, setN] = useState(premiere ? 4 : 10);
  const [p, setP] = useState(0.5);
  const [board, setBoard] = useState<GaltonState>(() => emptyBoard(premiere ? 4 : 10));
  const [k, setK] = useState(premiere ? 2 : 5);
  const [bt, setBt] = useState(false);
  const stats = useMemo(() => binomialStats(n, p), [n, p]);
  const cheb = useMemo(() => chebyshev(n, p, board), [n, p, board]);
  const showBt = (rank === null || rank >= 7) && bt;

  const reset = (nn: number) => {
    setBoard(emptyBoard(nn));
    setK((old) => Math.min(old, nn));
  };
  const changeN = (v: number) => {
    setN(v);
    reset(v);
  };
  const changeP = (v: number) => {
    setP(v);
    reset(n);
  };

  const pk = stats.law[k]!;
  const exact = exactPmf(n, k, p);
  const fk = board.total > 0 ? board.bins[k]! / board.total : null;
  const mean = sampleMean(board);
  const full = board.total >= MAX_BALLS;
  const pTex = tx(formatNumber(p, 2));
  const qTex = tx(formatNumber(1 - p, 2));
  const meanExact = rational(Math.round(n * p * 100), 100);
  // σ exact quand np(1 − p) est un carré (n = 4, p = 1/2 : σ = 1)
  const sdRel = Math.abs(stats.sd - Math.round(stats.sd * 1000) / 1000) < 1e-12 ? '=' : '\\approx';
  const valueTex = exact
    ? `= ${exact.tex}`
    : pk < 1e-4
      ? `\\approx ${texSci(pk)}`
      : `\\approx ${tx(formatNumber(pk, 4))}`;

  return (
    <div className="space-y-4">
      <p className="text-ink-600 text-sm">
        À chaque clou, la bille part à droite avec la probabilité p : le casier où elle tombe compte
        ses succès.
      </p>
      <div className="space-y-2">
        <Stepper
          label="Rangées de clous n"
          value={n}
          min={1}
          max={maxN}
          onChange={changeN}
          format={(v) => `n = ${v}`}
        />
        <ParamSlider
          name="p (aller à droite)"
          ariaLabel="Probabilité p d’aller à droite"
          value={p}
          min={0.05}
          max={0.95}
          step={0.05}
          format={(v) => formatNumber(v, 2)}
          onChange={changeP}
        />
      </div>

      <Board
        n={n}
        board={board}
        law={stats.law}
        picked={k}
        outside={showBt ? cheb.outside : null}
        onPick={setK}
      />

      <div className="flex flex-wrap gap-2" role="group" aria-label="Lâcher des billes">
        {BATCHES.map((b) => (
          <button
            key={b}
            type="button"
            disabled={full}
            aria-label={`Lâcher ${b} bille${b > 1 ? 's' : ''}`}
            onClick={() => setBoard((s) => dropBalls(s, n, p, b, rng))}
            className={cn(b === 1 ? PRIMARY_BUTTON : SECONDARY_BUTTON, 'disabled:opacity-40')}
          >
            {b === 1 ? 'Lâcher' : `× ${count(b)}`}
          </button>
        ))}
        <button
          type="button"
          disabled={board.total === 0}
          onClick={() => reset(n)}
          className={cn(SECONDARY_BUTTON, 'disabled:opacity-40')}
        >
          Effacer
        </button>
      </div>
      <p className="text-ink-500 text-sm" data-testid="gb-total">
        {count(board.total)} bille{board.total > 1 ? 's' : ''}
        {board.lastPath &&
          ` — la dernière : ${board.lastPath.map((r) => (r ? 'D' : 'G')).join(' ')}, casier ${board.lastPath.filter(Boolean).length}`}
      </p>

      <section
        className="border-ink-100 space-y-2 rounded-2xl border px-3 py-2"
        data-testid="gb-pmf"
      >
        <Stepper
          label="Casier k"
          value={k}
          min={0}
          max={n}
          onChange={setK}
          format={(v) => `k = ${v}`}
        />
        <MathText className="text-ink-900 block overflow-x-auto text-base">
          {premiere
            ? `$P(X = ${k}) = ${binomCoeff(n, k)} \\times ${pTex}^{${k}} \\times ${qTex}^{${n - k}} ${valueTex}$`
            : `$P(X = ${k}) = \\binom{${n}}{${k}} ${pTex}^{${k}} \\, ${qTex}^{${n - k}} ${valueTex}$`}
        </MathText>
        {premiere && (
          <p className="text-ink-600 text-sm">
            {binomCoeff(n, k)} chemin{binomCoeff(n, k) > 1 ? 's' : ''} de l’arbre mènent à {k}{' '}
            succès.
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-ink-700 min-w-0 flex-1 text-sm" data-testid="gb-freq">
            {fk === null
              ? 'Lâche des billes pour comparer avec la fréquence observée.'
              : `Fréquence observée du casier ${k} : ${formatNumber(fk, 3)}`}
          </p>
          {onSend && (exact || pk >= 1e-4) && (
            <SendToSheetButton
              label={exact ? exact.plain : formatNumber(pk, 4)}
              cost={sendCost}
              onClick={() => onSend({ atoms: exact ? exact.atoms : numberAtoms(pk, 4) })}
            />
          )}
        </div>
      </section>

      <section className="bg-ink-50 space-y-1 rounded-2xl px-3 py-2" data-testid="gb-stats">
        <div className="flex flex-wrap items-center gap-2">
          <MathText className="text-ink-900 min-w-0 flex-1 text-base">
            {premiere
              ? `$E(X) = \\sum k \\, P(X = k) = ${tx(meanExact.plain)}$`
              : `$E(X) = np = ${tx(meanExact.plain)}$`}
          </MathText>
          {onSend && (
            <SendToSheetButton
              label={meanExact.plain}
              cost={sendCost}
              onClick={() => onSend({ atoms: meanExact.atoms })}
            />
          )}
        </div>
        <MathText className="text-ink-900 block text-base">
          {premiere
            ? `$\\sigma(X) ${sdRel} ${tx(formatNumber(stats.sd, 3))}$`
            : `$\\sigma(X) = \\sqrt{np(1-p)} ${sdRel} ${tx(formatNumber(stats.sd, 3))}$`}
        </MathText>
        {mean !== null && (
          <p className="text-ink-700 text-sm">
            {board.total === 1
              ? `Une seule bille, tombée dans le casier ${mean}.`
              : `Moyenne des ${count(board.total)} billes : ${formatNumber(mean, 3)}.`}
          </p>
        )}
      </section>

      {(rank === null || rank >= 7) && (
        <div className="space-y-2">
          <ToggleChip on={bt} onClick={() => setBt((o) => !o)}>
            Bienaymé-Tchebychev
          </ToggleChip>
          {showBt && (
            <Note live={false}>
              <span data-testid="gb-bt">
                {cheb.outside.some(Boolean)
                  ? `Casiers rouges : |X − E| ≥ 2σ. Leur probabilité vaut ${formatNumber(cheb.exact, 4)} ≤ 1/4 = 0,25`
                  : `Ici, aucun casier n’est à 2σ ou plus de E : P(|X − E| ≥ 2σ) = 0 ≤ 1/4`}
                , comme l’annonce l’inégalité de Bienaymé-Tchebychev P(|X − E| ≥ 2σ) ≤ V/(2σ)² = 1/4
                {cheb.observed !== null &&
                  cheb.outside.some(Boolean) &&
                  ` ; fréquence observée : ${formatNumber(cheb.observed, 3)}`}
                . L’inégalité est vraie pour toute loi, d’où sa marge.
              </span>
            </Note>
          )}
        </div>
      )}
    </div>
  );
}

/** Clous, chemin de la dernière bille, casiers et histogramme (loi en pointillés). */
function Board({
  n,
  board,
  law,
  picked,
  outside,
  onPick,
}: {
  n: number;
  board: GaltonState;
  law: readonly number[];
  picked: number;
  outside: readonly boolean[] | null;
  onPick: (k: number) => void;
}) {
  const dx = Math.min(34, (W - 24) / (n + 1));
  const dy = Math.min(22, 150 / n);
  const cx = W / 2;
  const top = 18;
  const boardH = top + n * dy + 6;
  const histH = 130;
  const H = boardH + histH + 22;
  const freqs = board.bins.map((c) => (board.total ? c / board.total : 0));
  const yTop = Math.max(...law, ...freqs) * 1.12 || 1;
  const base = boardH + histH;
  const hy = (v: number) => base - (v / yTop) * (histH - 8);
  const binX = (k: number) => cx + (k - n / 2) * dx;

  let path = '';
  if (board.lastPath) {
    let j = 0;
    path = `M${cx},${top - 12}`;
    board.lastPath.forEach((right, i) => {
      const x = cx + (j - i / 2) * dx;
      const y = top + i * dy;
      path += `L${x.toFixed(1)},${(y - 5).toFixed(1)}`;
      if (right) j++;
    });
    path += `L${binX(j).toFixed(1)},${(boardH + 4).toFixed(1)}`;
  }

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={cn('border-ink-100 block w-full rounded-2xl border', FIGURE_BG_CLASS)}
      role="img"
      aria-label={`Planche de Galton à ${n} rangées ; histogramme des ${board.total} billes et loi binomiale en pointillés.`}
      data-testid="gb-board"
    >
      {Array.from({ length: n }, (_, i) =>
        Array.from({ length: i + 1 }, (_, j) => (
          <circle
            key={`${i}-${j}`}
            cx={cx + (j - i / 2) * dx}
            cy={top + i * dy}
            r={2.6}
            fill={INK}
          />
        )),
      )}
      {path && (
        <path
          d={path}
          fill="none"
          stroke={PATH}
          strokeWidth={2.5}
          strokeLinejoin="round"
          data-testid="gb-path"
        />
      )}
      <line x1={8} x2={W - 8} y1={base} y2={base} stroke={AXIS} strokeWidth={1.25} />
      {law.map((v, k) => {
        const x = binX(k) - dx * 0.4;
        const w = dx * 0.8;
        const isOut = outside?.[k] ?? false;
        return (
          <g key={k} onClick={() => onPick(k)} style={{ cursor: 'pointer' }}>
            <rect x={binX(k) - dx / 2} y={boardH} width={dx} height={histH} fill="transparent" />
            <rect
              x={x}
              y={hy(freqs[k]!)}
              width={w}
              height={base - hy(freqs[k]!)}
              fill={isOut ? OUT : BAR}
              fillOpacity={k === picked ? 0.95 : 0.6}
              rx={2}
            />
            <line
              x1={x - 2}
              x2={x + w + 2}
              y1={hy(v)}
              y2={hy(v)}
              stroke={LAW}
              strokeWidth={2.5}
              strokeDasharray="4 3"
            />
            {(n <= 10 || k % 2 === 0) && (
              <text
                x={binX(k)}
                y={H - 6}
                textAnchor="middle"
                fontSize={12}
                fontWeight={k === picked ? 700 : 400}
                fill={k === picked ? 'var(--color-ink-900)' : 'var(--color-ink-500)'}
              >
                {k}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
